import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { authMetadataQuery } from '@rootsight/shared';
import {
  Alert,
  Button,
  buttonClassName,
  CenteredPage,
  DescriptionList,
  LanguageSwitcher,
  Link,
  Logo,
  Spinner,
  Stack,
} from '@rootsight/ui';

import { api } from '../api';

import { securityContactQuery } from './securityContact';

/** Section is one titled block of the policy. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack gap={3} as="section">
      <h2>{title}</h2>
      {children}
    </Stack>
  );
}

function Items({ keys, prefix }: { keys: string[]; prefix: string }) {
  const { t } = useTranslation('security');
  return (
    <Stack as="ul" gap={2}>
      {keys.map((key) => (
        <li key={key}>{t(`${prefix}.${key}`)}</li>
      ))}
    </Stack>
  );
}

/** ReportingContact shows the contact the deployment publishes, if any. */
function ReportingContact() {
  const { t } = useTranslation('security');
  // The service says whether it serves a security.txt, so an unconfigured
  // deployment is not asked for a file it will answer with a logged 404. If
  // the answer cannot be had, ask for the file anyway.
  const metadata = useQuery(authMetadataQuery(api));
  const published = metadata.data?.securityTxt;
  const contact = useQuery({
    ...securityContactQuery,
    enabled: !metadata.isPending && published !== false,
  });

  if (metadata.isPending) return <Spinner />;
  if (published === false) return <Alert tone="info" title={t('reporting.noContact')} />;
  if (contact.isPending) return <Spinner />;
  if (contact.isError) {
    return (
      <Alert
        tone="warning"
        title={t('reporting.loadFailed')}
        action={
          <Button size="sm" onPress={() => void contact.refetch()}>
            {t('actions.retry', { ns: 'common' })}
          </Button>
        }
      />
    );
  }
  if (!contact.data) return <Alert tone="info" title={t('reporting.noContact')} />;
  const { kind, href, label } = contact.data;
  return (
    <DescriptionList
      items={[
        {
          label: t('reporting.contactLabel'),
          mono: true,
          value: <Link href={href}>{kind === 'email' ? <bdi dir="ltr">{label}</bdi> : label}</Link>,
        },
      ]}
    />
  );
}

/**
 * SecurityPage presents Rootsight's vulnerability disclosure policy. The
 * contact comes from the deployment's own /.well-known/security.txt, never
 * from this bundle.
 *
 * The wording and timelines were approved by the owner on 2026-10-12; see
 * docs/features/clustermax/12-platform-hygiene.md.
 */
export function SecurityPage() {
  const { t } = useTranslation(['security', 'common']);

  return (
    <CenteredPage
      brand={<Logo name={t('appName', { ns: 'common' })} />}
      title={t('title')}
      footer={<LanguageSwitcher />}
    >
      <Stack gap={6}>
        <Section title={t('scope.title')}>
          <p>{t('scope.intro')}</p>
          <Items prefix="scope" keys={['backend', 'console', 'sensor', 'installer']} />
          <p>{t('scope.outOfScope')}</p>
          <Items prefix="scope" keys={['notDos', 'notSocialEng', 'notAuthorized']} />
        </Section>

        <Section title={t('safeHarbour.title')}>
          <p>{t('safeHarbour.description')}</p>
          <Items prefix="safeHarbour" keys={['noLegal', 'noAuth', 'noAccess', 'responsible']} />
        </Section>

        <Section title={t('reporting.title')}>
          <p>{t('reporting.intro')}</p>
          <ReportingContact />
          <p>{t('reporting.guidelines')}</p>
          <Items prefix="reporting" keys={['describe', 'reproduce', 'impact', 'fix']} />
        </Section>

        <Section title={t('timeline.title')}>
          <DescriptionList
            items={(['acknowledge', 'triage', 'fix', 'disclosure'] as const).map((key) => ({
              label: t(`timeline.${key}`),
              value: t(`timeline.${key}Detail`),
            }))}
          />
        </Section>

        <Section title={t('announcement.title')}>
          <p>{t('announcement.description')}</p>
        </Section>

        {/* A full navigation, as on ErrorPage: this page may be served by Traefik. */}
        <a href="/" className={buttonClassName} data-variant="primary" data-size="lg">
          {t('actions.backToSignIn', { ns: 'common' })}
        </a>
      </Stack>
    </CenteredPage>
  );
}
